import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import client from '../../api/client';
import PTOBalanceCard from '../components/PTOBalanceCard';
import { NAVY, AMBER, AMBER_DARK, BORDER_COLOR, bubbleStyle, navyBtn } from '../../styles/tokens';

const STATUS_CONFIG = {
  pending:   { label: 'Pendiente',  color: '#eab308', bg: '#fef9c3', icon: Clock },
  approved:  { label: 'Aprobada',   color: '#16a34a', bg: '#dcfce7', icon: CheckCircle2 },
  denied:    { label: 'Denegada',   color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  cancelled: { label: 'Cancelada',  color: '#64748b', bg: '#f1f5f9', icon: AlertCircle },
};

const TYPE_LABELS = { vacation: 'Vacaciones', sick: 'Enfermedad', personal: 'Personal' };

export default function RequestListPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      client.get('/api/vacation/requests', { params: { view: 'my' } }),
      client.get('/api/vacation/balances'),
    ]).then(([reqRes, balRes]) => {
      setRequests(reqRes.data);
      setBalances(balRes.data);
    }).catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  const statusCounts = {
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    denied: requests.filter(r => r.status === 'denied').length,
    cancelled: requests.filter(r => r.status === 'cancelled').length,
  };

  function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;
  }

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}>Mis Vacaciones</h1>
        <button
          onClick={() => navigate('/vacaciones/nueva')}
          style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', fontSize: 13 }}
        >
          <Plus size={16} /> Nueva Solicitud
        </button>
      </div>

      <PTOBalanceCard balances={balances} />

      {/* Status summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { key: 'pending', label: 'Pendientes', count: statusCounts.pending, color: '#eab308', bg: '#fef9c3', icon: Clock },
          { key: 'approved', label: 'Aprobadas', count: statusCounts.approved, color: '#16a34a', bg: '#dcfce7', icon: CheckCircle2 },
          { key: 'denied', label: 'Denegadas', count: statusCounts.denied, color: '#dc2626', bg: '#fee2e2', icon: XCircle },
          { key: 'cancelled', label: 'Canceladas', count: statusCounts.cancelled, color: '#64748b', bg: '#f1f5f9', icon: AlertCircle },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              onClick={() => setFilter(s.key === filter ? 'all' : s.key)}
              style={{
                ...bubbleStyle, padding: '10px 12px', cursor: 'pointer',
                textAlign: 'center',
                border: filter === s.key ? `2px solid ${s.color}` : undefined,
              }}
            >
              <Icon size={18} color={s.color} style={{ marginBottom: 4 }} />
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Filter pills */}
      <div className="filter-bar" style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'Todas' },
          { key: 'pending', label: 'Pendientes' },
          { key: 'approved', label: 'Aprobadas' },
          { key: 'denied', label: 'Denegadas' },
          { key: 'cancelled', label: 'Canceladas' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '5px 14px', fontSize: 12, fontWeight: 600,
              borderRadius: 20, cursor: 'pointer',
              border: `1.5px solid ${filter === f.key ? AMBER : BORDER_COLOR}`,
              background: filter === f.key ? '#fef3c7' : '#fff',
              color: filter === f.key ? AMBER_DARK : '#64748b',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Requests list */}
      {filtered.length === 0 ? (
        <div style={{ ...bubbleStyle, padding: 40, textAlign: 'center', color: '#94a3b8' }}>
          <Calendar size={32} style={{ marginBottom: 8, opacity: 0.5 }} />
          <div style={{ fontSize: 14 }}>No hay solicitudes{filter !== 'all' ? ` ${STATUS_CONFIG[filter]?.label?.toLowerCase() || ''}s` : ''}.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(r => {
            const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
            const Icon = cfg.icon;
            return (
              <div
                key={r.id}
                onClick={() => navigate(`/vacaciones/${r.id}`)}
                style={{
                  ...bubbleStyle, padding: '14px 18px', cursor: 'pointer',
                  transition: 'box-shadow 0.15s',
                  display: 'flex', alignItems: 'center', gap: 14,
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: cfg.bg, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon size={18} color={cfg.color} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: NAVY }}>
                      {TYPE_LABELS[r.type] || r.type}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 8px',
                      borderRadius: 10, background: cfg.bg, color: cfg.color,
                    }}>
                      {cfg.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {formatDate(r.start_date)} — {formatDate(r.end_date)} · {parseFloat(r.days_requested)} día{parseFloat(r.days_requested) !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
